import { Navigate, Outlet, Route, Routes } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import { ChartTypeEnum } from './models/chart'
import CreateAccount from './pages/account/create-account'
import EditAccount from './pages/account/edit-account'
import AdminCharts from './pages/admin/admin-charts/admin-charts'
import AdminClerks from './pages/admin/admin-clerks/admin-clerks'
import AdminManageClerks from './pages/admin/admin-manage-clerks/admin-manage-clerks'
import AdminManageGeneralChart from './pages/admin/admin-manage-general-chart/admin-manage-general-chart'
import AdminManageNonPartisanChart from './pages/admin/admin-manage-non-partisan-chart/admin-manage-non-partisan-chart'
import AdminManagePartisanChart from './pages/admin/admin-manage-partisan-chart/admin-manage-partisan-chart'
import AdminManagePresidentialChart from './pages/admin/admin-manage-presidential-chart/manage-presidential-chart'
import { BallotMarkup } from './pages/admin/ballot-markup/ballot-markup'
import CreateRuleSet from './pages/admin/create-ruleset/create-ruleset'
import EditRule from './pages/admin/edit-rule/edit-rule'
import EditRuleSet from './pages/admin/edit-ruleset/edit-ruleset'
import EditClerk from './pages/clerk/edit-clerk'
import Charts from './pages/clerks/charts/charts'
import ManageGeneralChart from './pages/clerks/manage-general-chart/manage-general-chart'
import ManageChartNonPartisan from './pages/clerks/manage-non-partisan-chart/manage-chart'
import ManagePartisanChart from './pages/clerks/manage-partisan-chart/manage-partisan-chart'
import ManagePresidentialChart from './pages/clerks/manage-presidential-chart/manage-presidential-chart'
import ContactUs from './pages/contact-us'
import Instructions from './pages/instructions'
import Landing from './pages/landing'
import OrderPlaced from './pages/order/order-placed'
import ReviewInvoice from './pages/order/review-invoice'
import Rules from './pages/rules/rules'
import RuleSets from './pages/rulesets/rulesets'
import TermsOfService from './pages/terms-of-service'

export enum RoutePaths {
  LANDING = '/',
  ACCOUNT = '/account',
  TERMS = '/terms',
  CREATE_ACCOUNT = '/account/create',
  ACCOUNT_EDIT = '/account/edit',
  CONTACT_US = '/contact-us',
  VIEW_CLERK = '/clerks/:clerkId',
  EDIT_CLERK = '/clerk/edit/:clerkId',
  RULE_SETS = '/rulesets',
  EDIT_RULE_SET = '/rulesets/edit/:ruleSetId',
  CREATE_RULE_SET = '/rulesets/create',
  CREATE_RULE_SET_ID = '/rulesets/create/:ruleSetId',
  EDIT_RULE = '/rules/:ruleId',
  NON_PARTISAN_CHARTS = '/non-partisan',
  CREATE_NON_PARTISAN_CHART = '/non-partisan/create-chart',
  EDIT_NON_PARTISAN_CHART = '/non-partisan/edit-chart/:chartId',
  DUPLICATE_NON_PARTISAN_CHART = '/non-partisan/duplicate-chart/:chartId',
  GENERAL_CHARTS = '/general',
  CREATE_GENERAL_CHART = '/general/create-chart',
  EDIT_GENERAL_CHART = '/general/edit-chart/:chartId',
  DUPLICATE_GENERAL_CHART = '/general/duplicate-chart/:chartId',
  PARTISAN_CHARTS = '/partisan',
  CREATE_PARTISAN_CHART = '/partisan/create-chart',
  EDIT_PARTISAN_CHART = '/partisan/edit-chart/:chartId',
  DUPLICATE_PARTISAN_CHART = '/partisan/duplicate-chart/:chartId',
  PRESIDENTIAL_CHARTS = '/presidential',
  REVIEW_INVOICE = '/review-invoice/:typeId/:electionDate',
  ORDER_PLACED = '/order/:orderId',
  INSTRUCTIONS = '/instructions',
  GENERAL_ELECTIONS = '/elections/general',
  PARTISAN_ELECTIONS = '/elections/partisan',
  PRESIDENTIAL_ELECTIONS = '/elections/presidential',
  NON_PARTISAN_ELECTIONS = '/elections/non-partisan',
}

enum AccessLevel {
  ADMIN = 'ADMIN',
  NON_ADMIN = 'NON_ADMIN',
  USER = 'USER',
}

export type RouteInfo = {
  isUser: boolean
  isAdmin: boolean
  accessLevel: AccessLevel
}

export function ProtectedRoutes({ accessLevel, isUser, isAdmin }: RouteInfo) {
  if (accessLevel === AccessLevel.USER && !isUser) {
    return <Navigate to="/" />
  }
  if (accessLevel === AccessLevel.ADMIN && !isAdmin) {
    return <Navigate to="/" />
  }
  if (accessLevel === AccessLevel.NON_ADMIN && isUser && isAdmin) {
    return <Navigate to="/" />
  }
  if (accessLevel === AccessLevel.NON_ADMIN && !isUser) {
    return <Navigate to="/" />
  }
  return <Outlet />
}

export default function RouteList() {
  const { isLoggedIn, isAdmin } = useAuth()
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="terms-of-service" element={<TermsOfService />} />
      <Route path="account">
        <Route path="create" element={<CreateAccount />} />
        <Route path="edit" element={<EditAccount />} />
      </Route>

      <Route
        element={
          <ProtectedRoutes
            accessLevel={AccessLevel.USER}
            isAdmin={isAdmin}
            isUser={isLoggedIn}
          />
        }
      >
        <Route path="contact-us" element={<ContactUs />} />
      </Route>

      <Route
        element={
          <ProtectedRoutes
            accessLevel={AccessLevel.NON_ADMIN}
            isAdmin={isAdmin}
            isUser={isLoggedIn}
          />
        }
      >
        <Route path="instructions" element={<Instructions />} />
        <Route path="order">
          <Route path=":orderId" element={<OrderPlaced />} />
        </Route>
        <Route path="non-partisan">
          <Route
            index={true}
            element={<Charts chartType={ChartTypeEnum.NON_PARTISAN} />}
          />
          <Route
            index={false}
            path="create-chart"
            element={<ManageChartNonPartisan />}
          />
          <Route
            index={false}
            path="edit-chart/:chartId"
            element={<ManageChartNonPartisan />}
          />
          <Route
            index={false}
            path="duplicate-chart/:chartId"
            element={<ManageChartNonPartisan />}
          />
        </Route>
        <Route path="general">
          <Route
            index={true}
            element={<Charts chartType={ChartTypeEnum.GENERAL_ELECTION} />}
          />
          <Route
            index={false}
            path="create-chart"
            element={<ManageGeneralChart />}
          />
          <Route
            index={false}
            path="edit-chart/:chartId"
            element={<ManageGeneralChart />}
          />
          <Route
            index={false}
            path="duplicate-chart/:chartId"
            element={<ManageGeneralChart />}
          />
        </Route>
        <Route path="partisan">
          <Route
            index={true}
            element={<Charts chartType={ChartTypeEnum.PARTISAN_PRIMARY} />}
          />
          <Route
            index={false}
            path="create-chart"
            element={<ManagePartisanChart />}
          />
          <Route
            index={false}
            path="edit-chart/:chartId"
            element={<ManagePartisanChart />}
          />
          <Route
            index={false}
            path="duplicate-chart/:chartId"
            element={<ManagePartisanChart />}
          />
        </Route>
        {/*<Route path="presidential">
          <Route
            index={true}
            element={<Charts chartType={ChartTypeEnum.PRESIDENTIAL_PRIMARY} />}
          />
          <Route
            index={false}
            path="create-chart"
            element={<ManagePresidentialChart />}
          />
          <Route
            index={false}
            path="edit-chart/:chartId"
            element={<ManagePresidentialChart />}
          />
          <Route
            index={false}
            path="duplicate-chart/:chartId"
            element={<ManagePresidentialChart />}
          />
      </Route>*/}
        <Route
          path="review-invoice/:typeId/:electionDate"
          element={<ReviewInvoice />}
        />
      </Route>

      <Route
        element={
          <ProtectedRoutes
            accessLevel={AccessLevel.ADMIN}
            isAdmin={isAdmin}
            isUser={isLoggedIn}
          />
        }
      >
        <Route path="rulesets">
          <Route index={true} element={<RuleSets />} />
          <Route index={false} path="create">
            <Route index={true} element={<CreateRuleSet />} />
            <Route
              index={false}
              path=":rulesetId"
              element={<CreateRuleSet />}
            />
          </Route>
          <Route index={false} path=":rulesetId" element={<EditRuleSet />} />
        </Route>
        <Route path="rules">
          <Route index={true} element={<Rules />} />
          <Route index={false} path=":ruleId" element={<EditRule />} />
        </Route>
        <Route path="clerks">
          <Route index={true} element={<AdminClerks />} />
          <Route
            index={false}
            path=":clerkId"
            element={<AdminManageClerks />}
          />
          <Route index={false} path="edit/:clerkId" element={<EditClerk />} />
        </Route>
        <Route path="elections">
          <Route path="general">
            <Route
              index={true}
              element={
                <AdminCharts chartType={ChartTypeEnum.GENERAL_ELECTION} />
              }
            />
            <Route
              index={false}
              path="create"
              element={<AdminManageGeneralChart />}
            />
            <Route
              index={false}
              path="edit/:chartId"
              element={<AdminManageGeneralChart />}
            />
            <Route
              index={false}
              path="duplicate/:chartId"
              element={<AdminManageGeneralChart />}
            />
          </Route>
          <Route path="non-partisan">
            <Route
              index={true}
              element={<AdminCharts chartType={ChartTypeEnum.NON_PARTISAN} />}
            />
            <Route
              index={false}
              path="create"
              element={<AdminManageNonPartisanChart />}
            />
            <Route
              index={false}
              path="edit/:chartId"
              element={<AdminManageNonPartisanChart />}
            />
            <Route
              index={false}
              path="duplicate/:chartId"
              element={<AdminManageNonPartisanChart />}
            />
          </Route>
          <Route path="partisan">
            <Route
              index={true}
              element={
                <AdminCharts chartType={ChartTypeEnum.PARTISAN_PRIMARY} />
              }
            />
            <Route
              index={false}
              path="create"
              element={<AdminManagePartisanChart />}
            />
            <Route
              index={false}
              path="edit/:chartId"
              element={<AdminManagePartisanChart />}
            />
            <Route
              index={false}
              path="duplicate/:chartId"
              element={<AdminManagePartisanChart />}
            />
          </Route>
          <Route path="presidential">
            <Route
              index={true}
              element={
                <AdminCharts chartType={ChartTypeEnum.PRESIDENTIAL_PRIMARY} />
              }
            />
            <Route
              index={false}
              path="create"
              element={<AdminManagePresidentialChart />}
            />
            <Route
              index={false}
              path="edit/:chartId"
              element={<AdminManagePresidentialChart />}
            />
            <Route
              index={false}
              path="duplicate/:chartId"
              element={<AdminManagePresidentialChart />}
            />
          </Route>
        </Route>
        <Route path="mark-ballot/:chartId" element={<BallotMarkup />} />
      </Route>
    </Routes>
  )
}
